// src/app/dashboard/dashboard.component.ts
import { Component, OnInit, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { of, forkJoin, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';

import { UsuarioService } from '../../../services/usuario.service';
import { AuthService } from '../../../../../services/auth.service';
import * as L from 'leaflet';
import { environment } from '../../../../../../environments/environment';
import { ControllAppService } from '../../../../../services/controllApp.service';
import { IconManagerService } from '../../../services/icon-manager.service';
import { SolicitacaoService } from '../../../services/solicitacao.service';
import { ServicoService } from '../../../services/servico.service';
import { FinanceiroService } from '../../../services/financeiro.service';
import { UsuarioOnlineLocalizacao } from '../../../interfaces/UsuarioOnlineLocalizacao';
import { marker } from 'leaflet';

// Interfaces
export interface Colaborador {
  usuarioId: string;
  nome: string;
  empresa: string;
  horaEntrada?: string;
  horaSaida?: string;
  latitudeAtual?: string;
  longitudeAtual?: string;
  dataHoraUltimaAutenticacao?: Date;
  fotoUrl?: string;
  isOnline: boolean;
  dataHoraInicio?: string;
  totalPendentes?: number;
}

export interface DesempenhoColaborador {
  usuarioId: string;
  nome: string;
  nomeDaEmpresa: string;
  totalCadastrosCriados: number;
  totalCadastrosRecadastrados: number;
  totalPendentes?: number;
  tempoMedioConclusao: string;
  fotoUrl?: string;
  isOnline?: boolean;
}

export interface Vistoria {
  vistoriaId: string;
  usuarioId: string;
  clienteId: string;
  tipoVistoria: string;
  statusVistoria: string;
  numeroVistoria: string;
  dataHoraCadastro: string;
  atribuida: boolean;
  duracao?: string;
}

// New interface for vistoria response
export interface VistoriaDetalhada {
  vistoriaId: string;
  usuarioId: string;
  consumidorId: string;
  matricula?: string;
  dataVistoria?: string;
  temAssinatura?: boolean;
  assinaturaVistoriante?: string;
  dataHoraInicio?: string;  // Add this property
  dataHoraFim?: string;     // Add this property too for completeness
  tempoTotal?: string;      // Add this for calculating average time
}

// Updated interface for the API response
export interface CriarVistoriaResponseDto {
  itens?: Array<VistoriaDetalhada>;
  totalItens?: number;
  paginaAtual?: number;
  totalPaginas?: number;
  tamanhoPagina?: number;
  // Keep these for backward compatibility
  id?: string;
  usuarioId?: string;
  vistorias?: Array<Vistoria>;
  totalVistorias?: number;
  tempoMedio?: string;
}

// Add or update the UsuarioResponseDto interface
export interface UsuarioResponseDto {
  usuarioId: string;
  nome: string;
  email?: string;
  userName?: string;
  role?: string;
  ativo?: boolean;
  fotoUrl?: string;
  tipoUsuario?: string;
  cpf?: string;
  horaEntrada?: string;
  horaSaida?: string;
  horaAlmocoInicio?: string;
  horaAlmocoFim?: string;
  isOnline?: boolean;
  latitudeAtual?: string;
  longitudeAtual?: string;
  dataHoraUltimaAutenticacao?: string;
  numeroMatricula?: string;
  empresaId?: string;
  nomeDaEmpresa?: string;
  // Allow both nested object and string format for empresa
  empresa?: {
    empresaId?: string;
    ativo?: boolean;
    nomeDaEmpresa?: string;
    endereco?: string | null;
    tecnicos?: any[] | null;
  } | string;
}

// Add Solicitacao interface
export interface Solicitacao {
  solicitacaoId: string;
  usuarioId: string;
  dataHoraInicio?: string;
  dataHoraFim?: string;
  fotoInicio?: string;
  fotoFim?: string;
  // Add other properties as needed
}

export interface UserMetrics {
  usuarioId: string;
  nome: string;
  fotoUrl: string;
  nomeDaEmpresa: string;
  numeroVistorias: number;
  numeroSolicitacoes: number;
  numeroMarcacoes: number;
  tempoMedioConclusao: string;
  isOnline: boolean;
}

@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private map: L.Map | null = null;
  private markers: L.Marker[] = [];
  private updateIntervalId: any;
  defaultUserImage = 'assets/images/user-default.png'; // Default image path

  // Default map center coordinates (Rio de Janeiro)
  private defaultMapCenter: L.LatLngExpression = [-22.9068, -43.1729];
  private defaultZoomLevel: number = 10; // Start with a closer zoom level

  // Add properties to store map state
  private lastCenter: L.LatLng | null = null;
  private lastZoom: number | null = null;

  // Filtro de período
  periodoSelecionado: string = 'hoje';
  dataInicial: string = '';
  dataFinal: string = '';

  // Ranking-specific period (allows independent selection if desired)
  periodoRanking: string = 'custom';
  // Will be initialized to the first and last day of the current month in ngOnInit
  dataInicialRankingInput: string = '';
  dataFinalRankingInput: string = '';

  // Propriedades para os inputs (apenas data, sem horário)
  dataInicialInput: string = '2025-10-01';
  dataFinalInput: string = '2025-10-31';

  // Métricas
  totalOS: number = 0;
  osRealizadas: number = 0;
  osNaoAtribuidas: number = 0;
  osNaoRealizadas: number = 0;
  osPendentes: number = 0;
  efficiencyGeneral: number = 0;

  // Update metrics properties
  totalCriacoes: number = 0;
  totalRecadastramentos: number = 0;
  // Novas métricas solicitadas
  totalPendentes: number = 0;
  totalNegativas: number = 0;

  // Add new properties
  totalSolicitacoes: number = 0;
  solicitacoesRealizadas: number = 0;

  // Colaboradores
  colaboradoresDesempenho: DesempenhoColaborador[] = [];
  colaboradoresMetrics: UserMetrics[] = [];

  // Paginação
  paginaAtual: number = 1;
  itensPorPagina: number = 100;
  totalItems: number = 0;
  totalPaginas: number = 0;

  // Ranking (usuario counts)
  rankingUsuarios: Array<{ usuarioId: string; nome: string; fotoUrl?: string; total: number }> = [];
  // Loading flag for ranking
  isLoadingRanking: boolean = false;
  showFireworks: boolean = false;
  Math = Math;
  private filteredSolicitacoes: Solicitacao[] = [];
  colaboradores: Colaborador[] = [];
  usuariosOnlineComLocalizacao: UsuarioOnlineLocalizacao[] = [];

  constructor(
    private ngZone: NgZone,
    private usuarioService: UsuarioService,
    private httpClient: HttpClient,
    private controllAppService: ControllAppService,
    private iconManagerService: IconManagerService,
    private servicoService: ServicoService,
    private financeiroService: FinanceiroService,
    private authService: AuthService
  ) { }

  // Expose a method for template to check SuperAdmin
  public isSuperAdmin(): boolean {
    try { return this.authService.isSuperAdmin(); } catch (e) { return false; }
  }

  ngOnInit() {
    // Set default ranking period to current month's first and last day
    this.setDefaultRankingPeriod();

    this.initializeData();
    this.setupAutoRefresh();
    this.configurarIconesLeaflet(); // Configure Leaflet icons
    this.checkAndShowFireworks();
  }

  /**
   * Initialize the ranking date inputs to the first and last day of the current month
   * Format: YYYY-MM-DD (suitable for <input type="date">)
   */
  private setDefaultRankingPeriod(): void {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth(); // 0-based

    // First day of current month
    const firstDay = new Date(year, month, 1);
    const yyyy = firstDay.getFullYear();
    const mm = String(firstDay.getMonth() + 1).padStart(2, '0');
    const dd = String(firstDay.getDate()).padStart(2, '0');
    this.dataInicialRankingInput = `${yyyy}-${mm}-${dd}`;

    // Last day of current month
    const lastDayDate = new Date(year, month + 1, 0);
    const yyyy2 = lastDayDate.getFullYear();
    const mm2 = String(lastDayDate.getMonth() + 1).padStart(2, '0');
    const dd2 = String(lastDayDate.getDate()).padStart(2, '0');
    this.dataFinalRankingInput = `${yyyy2}-${mm2}-${dd2}`;
  }

  ngAfterViewInit(): void {
    this.initMap();
  }

  ngOnDestroy(): void {
    this.cleanupResources();
  }

  // Propriedade para colaboradores online
  get colaboradoresOnline(): DesempenhoColaborador[] {
    return this.colaboradoresDesempenho.filter(c => c.isOnline === true);
  }

  // Update the getter to use the new property
  get colaboradoresPerformanceForPeriod(): DesempenhoColaborador[] {
    return this.colaboradoresDesempenho;
  }

  // Métodos públicos para o template
  atualizarDados() {
    this.updateDateFilter();
    this.loadStatistics();
    this.loadColaboradores();
    this.loadRanking();
  }

  // Métodos para sincronizar os inputs de data
  onDataInicialChange(value: string): void {
    this.dataInicialInput = value;
    this.dataInicial = value ? value + 'T00:00:00' : '';
    this.atualizarDados();
  }

  onDataFinalChange(value: string): void {
    this.dataFinalInput = value;
    this.dataFinal = value ? value + 'T23:59:59' : '';
    this.atualizarDados();
  }

  mudarPagina(novaPagina: number): void {
    if (novaPagina > 0 && novaPagina <= this.totalPaginas && novaPagina !== this.paginaAtual) {
      this.paginaAtual = novaPagina;
      this.atualizarDados();
    }
  }

  mudarItensPorPagina(novoValor: number): void {
    this.itensPorPagina = novoValor;
    this.paginaAtual = 1; // Resetar para a primeira página
    this.calculateTotalPages();
    this.atualizarDados();
  }

  parseJornada(jornada: string): number {
    const valor = parseFloat(jornada.replace('%', '')) || 0;
    return Math.min(valor, 100);
  }

  getProgressWidth(jornada: string): number {
    return Math.min(this.parseJornada(jornada), 100);
  }

  // Inicializações
  private initializeData(): void {
    this.updateDateFilter();
    this.loadStatistics();
    this.loadColaboradores();
    this.loadRanking();
  }

  private setupAutoRefresh(): void {
    // Atualiza os dados a cada 30 segundos
    this.updateIntervalId = setInterval(() => {
      this.ngZone.run(() => {
        this.loadColaboradores();
      });
    }, 30000);
  }

  private cleanupResources(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    if (this.updateIntervalId) {
      clearInterval(this.updateIntervalId);
    }
    // Remove os marcadores do mapa
    this.markers.forEach(marker => marker.remove());
    this.markers = [];
  }

  // Filtro de período
  private updateDateFilter(): void {
    const dataAtual = new Date();

    switch (this.periodoSelecionado) {
      case 'hoje':
        this.dataInicial = this.formatDate(dataAtual);
        this.dataFinal = this.formatDate(dataAtual, true);
        this.dataInicialInput = this.dataInicial.split('T')[0];
        this.dataFinalInput = this.dataFinal.split('T')[0];
        break;
      case 'semana':
        const ultimaSemana = new Date(dataAtual);
        ultimaSemana.setDate(dataAtual.getDate() - 7);
        this.dataInicial = this.formatDate(ultimaSemana);
        this.dataFinal = this.formatDate(dataAtual, true);
        this.dataInicialInput = this.dataInicial.split('T')[0];
        this.dataFinalInput = this.dataFinal.split('T')[0];
        break;
      case 'mes':
        const ultimoMes = new Date(dataAtual);
        ultimoMes.setMonth(dataAtual.getMonth() - 1);
        this.dataInicial = this.formatDate(ultimoMes);
        this.dataFinal = this.formatDate(dataAtual, true);
        this.dataInicialInput = this.dataInicial.split('T')[0];
        this.dataFinalInput = this.dataFinal.split('T')[0];
        break;
      case 'custom':
        if (!this.dataInicial) {
          const primeiroDiaMes = new Date(dataAtual.getFullYear(), dataAtual.getMonth(), 1);
          this.dataInicial = this.formatDate(primeiroDiaMes);
          this.dataInicialInput = this.dataInicial.split('T')[0];
        }
        if (!this.dataFinal) {
          this.dataFinal = this.formatDate(dataAtual, true);
          this.dataFinalInput = this.dataFinal.split('T')[0];
        } else {
          // Garante que a data final sempre tenha o horário 23:59:59
          this.dataFinal = this.formatDate(new Date(this.dataFinal), true);
          this.dataFinalInput = this.dataFinal.split('T')[0];
        }
        break;
    }

  }

  public formatDate(data: Date | string, isEndDate: boolean = false): string {
    // Se data for string (do input date), use-a diretamente
    if (typeof data === 'string') {
      // Apenas adicionar o horário na string da data, mantendo o dia original
      const time = isEndDate ? 'T23:59:59' : 'T00:00:00';
      return data + time;
    }

    // Se for um objeto Date, use o método local para evitar problemas de timezone
    const localDate = new Date(data);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');

    const time = isEndDate ? 'T23:59:59' : 'T00:00:00';
    return `${year}-${month}-${day}${time}`;
  }

  private loadStatistics(): void {

    const dataInicio = this.formatDate(new Date(this.dataInicial));
    const dataFim = this.formatDate(new Date(this.dataFinal), true);

    this.usuarioService.getStatistics(dataInicio, dataFim).subscribe({
      next: (response) => {

        if (response) {
          const resp: any = response as any;

          this.totalCriacoes = resp.totalCriacoes ?? resp.total ?? 0;

          this.totalRecadastramentos = resp.totalRecadastramentos ?? resp.totalRecadastramento ?? 0;

          this.totalPendentes = resp.totalPendentes ?? resp.totalPendencia ?? resp.totalPendente ?? resp.totalPendentes ?? 0;

          this.totalNegativas = resp.totalNegativas ?? resp.totalNegativa ?? resp.totalNegadas ?? 0;

          this.osRealizadas = resp.totalRealizadas ?? resp.osRealizadas ?? this.osRealizadas ?? 0;
          this.totalOS = resp.total ?? this.totalOS ?? 0;
        } else {
          console.warn('⚠️ Resposta da API não contém dados');
          this.resetarContadores();
        }
      },
      error: (error) => {
        console.error('❌ Erro ao carregar estatísticas:', error);
        this.resetarContadores();
      }
    });
  }

  private resetarContadores(): void {
    this.totalCriacoes = 0;
    this.totalRecadastramentos = 0;
    this.totalPendentes = 0;
    this.totalNegativas = 0;
    this.osRealizadas = 0;
    this.totalOS = 0;
  }

  private getVistoriasByTodos(): Observable<CriarVistoriaResponseDto> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Create URL
    let url = `${environment.cedaeapp}/vistoria/obtertodos/`;

    // Add query parameters for date filtering
    let params = new HttpParams();

    if (this.periodoSelecionado === 'hoje') {
      // For 'hoje', we need to ensure we're getting today's vistorias
      // API might need both parameters to be the same date
      const today = this.formatDate(new Date());
      params = params
        .set('dataInicial', today)
        .set('dataFinal', today);

    } else {
      // For other periods, use the calculated dates
      params = params
        .set('dataInicial', this.dataInicial)
        .set('dataFinal', this.dataFinal);
    }

    return this.httpClient.get<CriarVistoriaResponseDto>(url, { headers, params }).pipe(
      tap((response: CriarVistoriaResponseDto) => {
        if (response.itens) {
          console.log(`📊 Total de vistorias: ${response.itens.length}`);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('❌ Erro ao consultar vistorias:', err);
        return of({
          itens: [],
          totalItens: 0,
          paginaAtual: 1,
          totalPaginas: 0,
          tamanhoPagina: 10
        } as CriarVistoriaResponseDto);
      })
    );
  }

  /**
   * Load vistorias and compute ranking by usuarioId.
   * Filters: status === 'Pendente' and statusOS contains 'conclu' (case-insensitive)
   */
  public loadRanking(): void {
    // Prepare date range based on periodoRanking
    let dataInicioParam = '';
    let dataFimParam = '';

    const now = new Date();
    switch (this.periodoRanking) {
      case 'hoje':
        dataInicioParam = this.formatDate(now);
        dataFimParam = this.formatDate(now, true);
        break;
      case 'semana':
        const semana = new Date(now);
        semana.setDate(now.getDate() - 7);
        dataInicioParam = this.formatDate(semana);
        dataFimParam = this.formatDate(now, true);
        break;
      case 'mes':
        const mes = new Date(now);
        mes.setMonth(now.getMonth() - 1);
        dataInicioParam = this.formatDate(mes);
        dataFimParam = this.formatDate(now, true);
        break;
      case 'custom':
        dataInicioParam = this.dataInicialRankingInput ? this.dataInicialRankingInput + 'T00:00:00' : '';
        dataFimParam = this.dataFinalRankingInput ? this.dataFinalRankingInput + 'T23:59:59' : '';
        break;
    }

    // Call FinanceiroService.getRankingVistoriadores with date filters
    this.isLoadingRanking = true;
    this.rankingUsuarios = [];

    const filters: { DataInicial?: string; DataFinal?: string } = {};
    if (dataInicioParam) filters.DataInicial = dataInicioParam;
    if (dataFimParam) filters.DataFinal = dataFimParam;

    this.financeiroService.getRankingVistoriadores(filters).subscribe({
      next: (resp) => {
        // resp is RankingVistoriador[] with fields: nomeVistoriante, quantidadeConcluidas, usuarioId
        if (Array.isArray(resp)) {
          this.rankingUsuarios = resp.map(r => ({
            usuarioId: r.usuarioId,
            nome: r.nomeVistoriante || '—',
            fotoUrl: r.fotoUrl ? this.processImageUrl(r.fotoUrl) : this.defaultUserImage,
            total: r.quantidadeConcluidas || 0
          })).sort((a, b) => b.total - a.total);
        } else {
          this.rankingUsuarios = [];
        }
        this.isLoadingRanking = false;

        // Check and trigger fireworks if conditions are met
        this.triggerFireworksIfNeeded();
      },
      error: (err) => {
        console.error('Erro ao carregar ranking via FinanceiroService:', err);
        this.rankingUsuarios = [];
        this.isLoadingRanking = false;
      }
    });
  }

  private getSolicitacoesByDateRange(): Observable<any> {
    const token = localStorage.getItem('token');
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    let url = `${environment.cedaeapp}/solicitacao/obtertodos/`;
    let params = new HttpParams();

    if (this.periodoSelecionado === 'hoje') {
      const today = this.formatDate(new Date());
      params = params
        .set('dataInicial', today)
        .set('dataFinal', today);

    } else {
      params = params
        .set('dataInicial', this.dataInicial)
        .set('dataFinal', this.dataFinal);

    }

    return this.httpClient.get(url, { headers, params }).pipe(
      tap((response: any) => {
        if (response.itens) {
        }
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('❌ Erro ao consultar solicitações:', err);
        return of({
          itens: [],
          totalItens: 0,
          paginaAtual: 1,
          totalPaginas: 0,
          tamanhoPagina: 10
        });
      })
    );
  }

  private loadColaboradores(): void {
    this.usuarioService.getStatisticsUsuarios(this.paginaAtual, this.itensPorPagina)
      .subscribe({
        next: (response) => {
          if (response && response.itens) {
            // Store the metrics directly from API
            this.colaboradoresDesempenho = response.itens.map((item: any) => ({
              usuarioId: item.usuarioId,
              nome: item.nome,
              nomeDaEmpresa: item.nomeDaEmpresa,
              totalCadastrosCriados: item.totalCadastrosCriados,
              totalCadastrosRecadastrados: item.totalCadastrosRecadastrados,
              tempoMedioConclusao: item.tempoMedioConclusao,
              totalPendentes: item.totalPendentes ?? item.totalPendente ?? 0,
              fotoUrl: this.processImageUrl(item.fotoUrl),
              isOnline: item.isOnline
            }));

            // Atualizar também a lista de colaboradores para o mapa
            this.colaboradores = response.itens.map((item: UserMetrics) => ({
              usuarioId: item.usuarioId,
              nome: item.nome,
              empresa: item.nomeDaEmpresa,
              fotoUrl: this.processImageUrl(item.fotoUrl),
              // Map pending counts for use in popups or later features
              totalPendentes: (item as any).totalPendentes ?? 0,
              isOnline: item.isOnline,
              latitudeAtual: '', // Preencher esses valores depois se necessário
              longitudeAtual: ''
            }));

            // Update pagination
            this.totalItems = response.totalItens;
            this.totalPaginas = response.totalPaginas;

            // Update map markers if needed
            if (this.map) {
              this.updateMapMarkers();
            }
          } else {
            console.warn('⚠️ Resposta da API não contém itens');
            this.colaboradoresDesempenho = [];
            this.totalItems = 0;
            this.totalPaginas = 0;
          }
        },
        error: (error) => {
          console.error('❌ Erro ao carregar métricas dos colaboradores:', error);
          this.colaboradoresDesempenho = [];
          this.totalItems = 0;
          this.totalPaginas = 0;
        }
      });
  }

  // Helper method to process image URLs
  private processImageUrl(fotoUrl: string): string {
    if (!fotoUrl) return this.defaultUserImage;

    const url = fotoUrl.trim();

    // If it's already a full URL or a data URI, return as-is
    if (/^https?:\/\//i.test(url) || /^data:/i.test(url)) {
      return url;
    }

    // Protocol-relative URL (e.g. //cdn.example.com/image.png)
    if (url.startsWith('//')) {
      return window.location.protocol + url;
    }

    // Ensure we don't duplicate slashes when joining base URL and path
    const base = (environment.apiBaseUrl || '').replace(/\/$/, '');

    if (url.startsWith('/')) {
      return `${base}${url}`;
    }

    // Otherwise, treat as relative path and insert a slash between base and path
    return `${base}/${url}`;
  }

  // Mapa
  // Método para inicializar o mapa com ponto central padrão
  private initMap(): void {
    setTimeout(() => {
      try {
        const mapElement = document.getElementById('map');
        if (!mapElement) {
          console.error('Elemento com id="map" não encontrado.');
          return;
        }

        mapElement.style.minHeight = '400px';

        this.map = L.map('map', {
          center: this.defaultMapCenter, // Usa o centro padrão configurável
          zoom: this.defaultZoomLevel    // Usa o zoom padrão configurável
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '© OpenStreetMap'
        }).addTo(this.map);

        // Adiciona um marcador no ponto central padrão usando o sistema de ícones existente
        // Usa o ícone vermelho (offline) para o marcador padrão
        const defaultIcon = this.iconManagerService.getLeafletIconByStatus(false, 32);

        const defaultMarker = L.marker(this.defaultMapCenter, { icon: defaultIcon });


        this.map.whenReady(() => {
          this.updateMapMarkers();
        });

        // Capture map state changes
        this.map.on("moveend", () => {
          this.lastCenter = this.map!.getCenter();
          this.lastZoom = this.map!.getZoom();
          // Optional: persist in localStorage
          localStorage.setItem("mapCenter", JSON.stringify(this.lastCenter));
          localStorage.setItem("mapZoom", this.lastZoom.toString());
        });

        // Restore saved state
        const savedCenter = localStorage.getItem("mapCenter");
        const savedZoom = localStorage.getItem("mapZoom");

        if (savedCenter && savedZoom) {
          const { lat, lng } = JSON.parse(savedCenter);
          this.map.setView([lat, lng], parseInt(savedZoom));
        }
      } catch (error) {
        console.error('Erro ao inicializar mapa:', error);
      }
    }, 300);
  }

  private updateMapMarkers(): void {
    if (!this.map) {
      console.error('Map not initialized');
      return;
    }
    // Assign this.map to a variable after the null check
    const mapInstance = this.map;

    // Remove existing markers using the guaranteed non-null mapInstance
    this.markers.forEach(marker => marker.remove());
    this.markers = [];

    // Buscar TODOS os colaboradores, não apenas os online
    this.usuarioService.carregarTodosColaboradores().subscribe({
      next: (colaboradores) => {
        // Filtrar apenas os colaboradores com coordenadas válidas
        const colaboradoresValidos = colaboradores.filter(colaborador => {
          const isValid = colaborador.latitudeAtual &&
            colaborador.longitudeAtual &&
            colaborador.latitudeAtual !== '0' &&
            colaborador.longitudeAtual !== '0';

          return isValid; // Incluir todos os colaboradores com coordenadas válidas
        });

        // Group collaborators by coordinates to detect overlaps
        const coordGroups: { [key: string]: any[] } = {};

        colaboradoresValidos.forEach(colaborador => {
          const lat = parseFloat(colaborador.latitudeAtual);
          const lng = parseFloat(colaborador.longitudeAtual);
          if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;

          // Use a string key to group by coordinates (with some precision)
          const coordKey = `${lat.toFixed(5)},${lng.toFixed(5)}`;

          if (!coordGroups[coordKey]) {
            coordGroups[coordKey] = [];
          }
          coordGroups[coordKey].push(colaborador);
        });

        // Process each coordinate group
        Object.entries(coordGroups).forEach(([coordKey, groupMembers]) => {
          // Sort group members so online collaborators are processed last (they'll appear on top)
          groupMembers.sort((a, b) => {
            if (a.isOnline === b.isOnline) return 0;
            return a.isOnline ? 1 : -1; // Online colaboradores ao final da array
          });

          // Apply offset to each member if there's more than one at this location
          groupMembers.forEach((colaborador, index) => {
            try {
              const baseLatLng = coordKey.split(',').map(parseFloat);
              const [baseLat, baseLng] = baseLatLng;

              // Apply spiral offset if there are multiple collaborators at this location
              let lat = baseLat;
              let lng = baseLng;

              // Only apply offset if there's more than one collaborator at this position
              if (groupMembers.length > 1 && index > 0) {
                const offset = this.calculateSpiralOffset(index, 0.0005); // ~50m offset at equator
                lat = baseLat + offset.lat;
                lng = baseLng + offset.lng;
              }

              // Usar ícone verde para colaboradores online e vermelho para offline
              const icon = this.iconManagerService.getLeafletIconByStatus(colaborador.isOnline);

              // Create marker with popup and prioritize online users with higher z-index
              const markerOptions = {
                icon: icon,
                zIndexOffset: colaborador.isOnline ? 1000 : 0 // Give higher z-index to online markers
              };

              const markerInstance = L.marker([lat, lng], markerOptions);
              markerInstance.bindPopup(`
                <div style="min-width: 150px; display: flex; align-items: center; gap: 10px;">
                  <img src="${colaborador.fotoUrl}" alt="${colaborador.nome}" style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.1);" onerror="this.src='${this.defaultUserImage}';">
                  <div style="flex: 1;">
                    <strong style="display: block; font-size: 0.9rem;">${colaborador.nome}</strong>
                    <em style="font-size: 0.8rem; color: #555;">${colaborador.empresaNome || 'Empresa não especificada'}</em><br>
                    <span style="color: ${colaborador.isOnline ? '#22c55e' : '#ef4444'}; font-size: 0.8rem;">● ${colaborador.isOnline ? 'Online' : 'Offline'}</span><br>
                    <small style="font-size: 0.7rem; color: #777;">Última localização: ${lat.toFixed(4)}, ${lng.toFixed(4)}</small><br>
                    <a href="/admin/roteiro-colaborador?usuarioId=${colaborador.usuarioId}" style="display: inline-block; margin-top: 5px; padding: 3px 6px; background: #3b82f6; color: white; text-decoration: none; border-radius: 4px; font-size: 0.75rem;">
                      Ver Roteiro
                    </a>
                  </div>
                </div>
              `);

              // Use the non-null mapInstance variable here
              markerInstance.addTo(mapInstance);
              this.markers.push(markerInstance);

            } catch (error) {
              console.error(`Erro ao adicionar marcador para ${colaborador.nome}:`, error);
            }
          });
        });

        // Adjust map view based on markers
        if (this.lastCenter && this.lastZoom) {
          // Maintain user's view
          mapInstance.setView(this.lastCenter, this.lastZoom);
        } else {
          // Only apply automatic adjustment if no saved state exists
          if (this.markers.length > 0) {
            if (this.markers.length === 1) {
              const marker = this.markers[0];
              mapInstance.setView(marker.getLatLng(), 13);
            } else {
              const group = L.featureGroup(this.markers);
              mapInstance.fitBounds(group.getBounds(), {
                padding: [50, 50],
                maxZoom: 13
              });
            }
          } else {
            mapInstance.setView(this.defaultMapCenter, this.defaultZoomLevel);
          }
        }
      },
      error: (error) => {
        console.error('❌ Erro ao buscar colaboradores:', error);
      }
    });
  }

  /**
   * Calculates a spiral offset for markers at the same location
   * @param index The index of the marker in the group
   * @param baseOffset The base offset distance (in degrees)
   * @returns Object with lat and lng offset values
   */
  private calculateSpiralOffset(index: number, baseOffset: number = 0.0002): { lat: number, lng: number } {
    // Use a spiral pattern to distribute markers around the original point
    // This formula creates a growing spiral as index increases
    const angle = index * 2.4; // Radians, controls how far apart the points are in the spiral
    const radius = baseOffset * Math.sqrt(index); // Increasing radius for spiral effect

    return {
      lat: radius * Math.cos(angle),
      lng: radius * Math.sin(angle)
    };
  }

  private calculateTotalPages(): void {
    this.totalPaginas = Math.ceil(this.totalItems / this.itensPorPagina);
  }

  private configurarIconesLeaflet(): void {
    const iconRetinaUrl = 'assets/images/marker-icon-2x.png';
    const iconUrl = 'assets/images/marker-icon.png';
    const shadowUrl = 'assets/images/marker-shadow.png';

    const iconDefault = L.icon({
      iconRetinaUrl: iconRetinaUrl,
      iconUrl: iconUrl,
      shadowUrl: shadowUrl,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      tooltipAnchor: [16, -28],
      shadowSize: [41, 41]
    });

    L.Marker.prototype.options.icon = iconDefault;
  }

  handleImageError(event: Event): void {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = this.defaultUserImage;
    imgElement.onerror = null;
  }

  // Add this new method
  formatTimeString(value: string | null | undefined): string {
    if (!value) return '-';

    // Remove milliseconds if present
    const parts = value.split('.');
    const timeOnly = parts[0];

    // Check if it matches HH:mm:ss format
    if (timeOnly.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeOnly;
    }

    return value;
  }

  /**
   * Retorna a porcentagem (string) de um valor em relação ao total.
   * Formato: "12%". Retorna "0%" quando total é zero ou não disponível.
   */
  public getPercent(value: number | null | undefined): string {
    return `${this.getPercentValue(value)}%`;
  }

  /**
   * Retorna a porcentagem numérica (0-100) de um valor em relação ao total.
   */
  public getPercentValue(value: number | null | undefined): number {
    const total = this.totalOS || this.totalCriacoes || 0;
    const val = value ?? 0;

    if (!total || total <= 0) return 0;

    const pct = Math.round((val / total) * 100);
    return Math.min(Math.max(pct, 0), 100);
  }

  // Check if we should show fireworks
  private checkAndShowFireworks(): void {

    // Get today's date (without time) - using local timezone
    const today = new Date();
    const todayString = today.getFullYear() + '-' +
      String(today.getMonth() + 1).padStart(2, '0') + '-' +
      String(today.getDate()).padStart(2, '0');

    // Check if end date is today (comparing strings to avoid timezone issues)
    if (this.dataFinalRankingInput === todayString) {
      // Check localStorage to see if fireworks were already shown for this period
      const storageKey = `fireworks_shown_${this.dataInicialRankingInput}_${this.dataFinalRankingInput}`;
      const alreadyShown = localStorage.getItem(storageKey);


      if (!alreadyShown && this.rankingUsuarios.length > 0) {

        this.showFireworks = true;

        // Save to localStorage
        localStorage.setItem(storageKey, 'true');

        // Hide fireworks after 6 seconds
        setTimeout(() => {

          this.showFireworks = false;
        }, 6000);
      } else {
        if (alreadyShown) {
          console.log('❌ Fogos já foram mostrados para este período');
        }
        if (this.rankingUsuarios.length === 0) {
          console.log('❌ Não há vencedores no ranking');
        }
      }
    } else {
      console.log('❌ Data final não é hoje');
    }
  }

  // Call this method after loading ranking
  private triggerFireworksIfNeeded(): void {
    this.checkAndShowFireworks();
  }

  // Close fireworks manually
  public closeFireworks(): void {
    this.showFireworks = false;
  }
}